/**
 * widgetMediaForms — shared image + embed config sub-forms (Story 37.3).
 *
 * The single source of truth for the report editor's image-upload and
 * embed-builder controls. Extracted (MOVED, not copied) out of
 * `WidgetConfigPanel.tsx` so that BOTH the widget config panel (editing an
 * existing widget) AND the Phase-2 category pickers (`ImagesCategoryPanel` /
 * `EmbedsCategoryPanel`, configure-then-add) consume one definition and cannot
 * drift apart. (Phase-2 anti-drift mandate / AC #3 / AC #8.)
 *
 * Contract — UNCHANGED from the original (do NOT refactor to `{config,onChange}`):
 * both sub-forms take `{ widget: WidgetConfig; onUpdate: (id, changes) => void }`
 * and read/write `widget.config`. The config panel passes the real selected
 * widget; the category panels pass a synthetic draft-widget + a draft `onUpdate`
 * adapter (see ImagesCategoryPanel / EmbedsCategoryPanel).
 *
 * LEAF MODULE (Pre-mortem E): this file must NOT import from
 * `WidgetConfigPanel.tsx` / `WidgetPalette.tsx`. Its only deps are `WidgetConfig`
 * (lib/templateUtils), `apiPost`/`apiPatch` (lib/apiClient), and lucide icons —
 * all leaf deps, no import cycle.
 *
 * Originally Story 5.25 (image) + Story 5.26 (embed); moved here verbatim by
 * Story 37.3 (behavior byte-for-byte unchanged — AC #5).
 */

import { useState, useCallback, useEffect } from "react";
import { Upload, X as XIcon, Replace } from "lucide-react";
import type { WidgetConfig } from "../../lib/templateUtils";
import { apiPost, apiPatch } from "../../lib/apiClient";
import { SelectField } from "./SelectField";

// ── Image Config Section (Story 5.25) ────────────────────────────────

export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

export function ImageConfigSection({
  widget,
  onUpdate,
}: {
  widget: WidgetConfig;
  onUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
}) {
  const config = widget.config as Record<string, unknown>;
  const imageUrl = (config.imageUrl as string) ?? "";
  const fit = (config.fit as string) ?? "contain";
  const align = (config.align as string) ?? "center";
  const alt = (config.alt as string) ?? "";
  const caption = (config.caption as string) ?? "";

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleImageConfigChange = useCallback(
    (key: string, value: string) => {
      onUpdate(widget.id, {
        config: { ...widget.config, [key]: value },
      });
    },
    [widget.id, widget.config, onUpdate]
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (uploading) return; // M4: prevent concurrent uploads
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setUploadError("Invalid file type. Use JPEG, PNG, or WebP.");
        return;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        setUploadError("File too large. Maximum size is 5MB.");
        return;
      }

      setUploadError(null);
      setUploading(true);

      try {
        // Step 1: Get presigned URL
        const urlResponse = await apiPost("/api/reports/image-upload-url", {
          contentType: file.type,
          fileSize: file.size,
        });
        const urlJson = await urlResponse.json();
        const { uploadUrl, imageKey } = urlJson.data as { uploadUrl: string; imageKey: string };

        // Step 2: Upload directly to R2
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error("Upload to storage failed");
        }

        // Step 3: Confirm upload
        const confirmResponse = await apiPatch("/api/reports/image-confirm", {
          imageKey,
        });
        const confirmJson = await confirmResponse.json();
        const { imageUrl: confirmedUrl } = confirmJson.data as { imageUrl: string };

        // Step 4: Update widget config
        onUpdate(widget.id, {
          config: { ...widget.config, imageUrl: confirmedUrl },
        });
      } catch {
        setUploadError("Upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    },
    [widget.id, widget.config, onUpdate, uploading]
  );

  /** M1: Opens a file picker without leaking DOM nodes */
  const openFilePicker = useCallback(() => {
    if (uploading) return; // M4: prevent concurrent uploads
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ALLOWED_IMAGE_TYPES.join(",");
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) handleFileSelect(file);
      input.remove(); // M1: clean up DOM node
    };
    input.click();
  }, [uploading, handleFileSelect]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleRemoveImage = useCallback(() => {
    onUpdate(widget.id, {
      config: { ...widget.config, imageUrl: "" },
    });
  }, [widget.id, widget.config, onUpdate]);

  return (
    <div className="border-t border-input/50 pt-3" data-testid="image-config">
      <h4 className="text-[13.5px] font-medium text-foreground mb-3">
        Image Settings
      </h4>

      {/* Upload zone / Preview */}
      {!imageUrl ? (
        <div
          data-testid="image-upload-zone"
          role="button"
          tabIndex={0}
          aria-label="Upload image"
          className={`mb-3 border-[0.5px] border-dashed border-input rounded-lg p-4 text-center transition-colors ${uploading ? "opacity-50 cursor-wait" : "cursor-pointer hover:bg-accent/50"}`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={openFilePicker}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openFilePicker(); } }}
        >
          <Upload className="mx-auto mb-2 text-muted-foreground" size={20} />
          <p className="text-xs text-muted-foreground">
            {uploading ? "Uploading..." : "Drop image or click to browse"}
          </p>
          <p className="text-[11px] text-muted-foreground/60 mt-1">
            JPEG, PNG, WebP • Max 5MB
          </p>
        </div>
      ) : (
        <div className="mb-3" data-testid="image-preview-section">
          <img
            src={imageUrl}
            alt={alt || "Preview"}
            className="w-full max-h-32 object-contain rounded-lg border-[0.5px] border-input mb-2"
            data-testid="config-image-preview"
          />
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="image-replace-btn"
              className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-lg border-[0.5px] border-input hover:bg-accent transition-colors"
              onClick={openFilePicker}
              disabled={uploading}
            >
              <Replace size={12} />
              Replace
            </button>
            <button
              type="button"
              data-testid="image-remove-btn"
              className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-lg border-[0.5px] border-input hover:bg-destructive/10 text-destructive transition-colors"
              onClick={handleRemoveImage}
            >
              <XIcon size={12} />
              Remove
            </button>
          </div>
        </div>
      )}

      {uploading && (
        <div className="mb-3 flex items-center gap-2" data-testid="image-upload-progress">
          <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-muted-foreground">Uploading...</p>
        </div>
      )}

      {uploadError && (
        <p className="mb-3 text-xs text-destructive" data-testid="image-upload-error">
          {uploadError}
        </p>
      )}

      {/* Fit mode */}
      <div className="mb-3">
        <label htmlFor="image-fit" className="text-[13px] text-foreground">
          Fit Mode
        </label>
        <div className="mt-1">
          <SelectField
            ariaLabel="Fit Mode"
            testId="image-fit-select"
            value={fit}
            onChange={(v) => handleImageConfigChange("fit", v)}
            triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
            options={[
              { value: "contain", label: "Contain (letterboxed)" },
              { value: "cover", label: "Cover (cropped to fill)" },
              { value: "fill", label: "Fill (stretched)" },
            ]}
          />
        </div>
      </div>

      {/* Alignment */}
      <div className="mb-3">
        <label className="text-[13px] text-foreground">Alignment</label>
        <div className="flex gap-1 mt-1">
          {(["left", "center", "right"] as const).map((a) => (
            <button
              key={a}
              data-testid={`image-align-${a}`}
              className={`flex-1 py-1.5 text-xs rounded-lg border-[0.5px] transition-colors ${align === a ? "bg-foreground/10 text-foreground border-input" : "bg-background text-muted-foreground border-input hover:bg-accent/50"}`}
              onClick={() => handleImageConfigChange("align", a)}
            >
              {a.charAt(0).toUpperCase() + a.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Alt text */}
      <div className="mb-3">
        <label htmlFor="image-alt" className="text-[13px] text-foreground">
          Alt Text <span className="text-muted-foreground/50">(recommended)</span>
        </label>
        <input
          id="image-alt"
          data-testid="image-alt-input"
          type="text"
          value={alt}
          maxLength={200}
          onChange={(e) => handleImageConfigChange("alt", e.target.value)}
          placeholder="Describe the image..."
          className="mt-1 h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
        />
      </div>

      {/* Caption */}
      <div className="mb-3">
        <label htmlFor="image-caption" className="text-[13px] text-foreground">
          Caption <span className="text-muted-foreground/50">(optional)</span>
        </label>
        <input
          id="image-caption"
          data-testid="image-caption-input"
          type="text"
          value={caption}
          maxLength={200}
          onChange={(e) => handleImageConfigChange("caption", e.target.value)}
          placeholder="Figure 1: Description..."
          className="mt-1 h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
        />
      </div>
    </div>
  );
}

// ── Embed Config Section (Story 5.26) ────────────────────────────────

export function EmbedConfigSection({
  widget,
  onUpdate,
}: {
  widget: WidgetConfig;
  onUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
}) {
  const config = widget.config as Record<string, unknown>;
  const url = (config.url as string) ?? "";
  const embedUrl = (config.embedUrl as string) ?? "";
  const provider = (config.provider as string) ?? "Unknown";
  const title = (config.title as string) ?? "";
  const aspectRatio = (config.aspectRatio as string) ?? "16:9";
  const showBorder = (config.showBorder as boolean) ?? true;

  const [urlDraft, setUrlDraft] = useState(url);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [domainWarning, setDomainWarning] = useState<string | null>(null);

  // Sync draft when external config changes
  useEffect(() => {
    setUrlDraft(url);
  }, [url]);

  const handlePreview = useCallback(() => {
    const trimmed = urlDraft.trim();
    if (!trimmed) {
      setUrlError("URL is required");
      return;
    }

    // Validate URL format
    try {
      new URL(trimmed);
    } catch {
      setUrlError("Invalid URL format");
      return;
    }

    // HTTPS-only check
    if (!trimmed.startsWith("https://")) {
      setUrlError("Only HTTPS URLs are allowed");
      return;
    }

    setUrlError(null);

    // Auto-detect provider and transform URL
    // Kept in sync with EMBED_ALLOWED_DOMAINS from @provenleap/db/report-layout-types
    // (web cannot import db directly — no cross-package dependency)
    let isAllowed = false;
    try {
      const hostname = new URL(trimmed).hostname;
      const ALLOWED = [
        "youtube.com", "www.youtube.com", "youtu.be", "youtube-nocookie.com",
        "player.vimeo.com", "vimeo.com", "docs.google.com", "sheets.google.com",
        "slides.google.com", "drive.google.com", "loom.com", "www.loom.com",
        "figma.com", "www.figma.com", "canva.com", "www.canva.com", "miro.com",
        "airtable.com", "notion.so", "www.notion.so", "calendly.com", "typeform.com",
      ];
      isAllowed = ALLOWED.includes(hostname);
    } catch {
      // Invalid URL already caught above
    }

    if (!isAllowed) {
      setDomainWarning("This URL may not support embedding. Preview to verify.");
    } else {
      setDomainWarning(null);
    }

    // Transform URL using simple client-side logic
    const transformed = transformUrl(trimmed);

    onUpdate(widget.id, {
      config: {
        ...widget.config,
        url: trimmed,
        embedUrl: transformed.embedUrl,
        provider: transformed.provider,
      },
    });
  }, [urlDraft, widget.id, widget.config, onUpdate]);

  const handleEmbedConfigChange = useCallback(
    (key: string, value: unknown) => {
      onUpdate(widget.id, {
        config: { ...widget.config, [key]: value },
      });
    },
    [widget.id, widget.config, onUpdate]
  );

  return (
    <div className="space-y-3" data-testid="embed-config-section">
      {/* URL Input */}
      <div>
        <label htmlFor="embed-url" className="block text-[13px] text-foreground">
          URL
        </label>
        <div className="mt-1 flex gap-1.5">
          <input
            id="embed-url"
            type="text"
            data-testid="embed-url-input"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePreview();
            }}
            placeholder="Paste URL"
            className="h-9 flex-1 rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
          />
          <button
            data-testid="embed-preview-btn"
            onClick={handlePreview}
            className="h-9 px-2.5 text-[13.5px] font-medium rounded-lg bg-foreground text-background hover:bg-foreground/90"
          >
            Preview
          </button>
        </div>
        {urlError && (
          <p className="mt-1 text-xs text-destructive" data-testid="embed-url-error">{urlError}</p>
        )}
        {domainWarning && !urlError && (
          <p className="mt-1 text-xs text-yellow-600" data-testid="embed-domain-warning">{domainWarning}</p>
        )}
      </div>

      {/* Provider badge */}
      {embedUrl && (
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 bg-muted rounded text-xs font-medium" data-testid="embed-config-provider">
            {provider}
          </span>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
            data-testid="embed-open-original"
          >
            Open in new tab
          </a>
        </div>
      )}

      {/* Aspect Ratio */}
      <div>
        <label className="block text-[13px] text-foreground mb-1">
          Aspect Ratio
        </label>
        <div className="flex gap-1" data-testid="embed-aspect-ratio-group">
          {(["16:9", "4:3", "1:1"] as const).map((ar) => (
            <button
              key={ar}
              data-testid={`embed-ar-${ar}`}
              className={`flex-1 py-1.5 text-xs font-medium rounded-lg border-[0.5px] ${
                aspectRatio === ar
                  ? "bg-foreground/10 text-foreground border-input"
                  : "bg-background text-muted-foreground border-input hover:bg-accent/50"
              }`}
              onClick={() => handleEmbedConfigChange("aspectRatio", ar)}
            >
              {ar}
            </button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div>
        <label htmlFor="embed-title" className="block text-[13px] text-foreground">
          Title (optional)
        </label>
        <input
          id="embed-title"
          type="text"
          data-testid="embed-title-input"
          value={title}
          onChange={(e) => handleEmbedConfigChange("title", e.target.value)}
          placeholder="Enter a display title..."
          maxLength={200}
          className="mt-1 h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
        />
      </div>

      {/* Show Border */}
      <div className="flex items-center justify-between">
        <label htmlFor="embed-border" className="text-[13px] text-foreground">
          Show Border
        </label>
        <button
          id="embed-border"
          data-testid="embed-border-toggle"
          role="switch"
          aria-checked={showBorder}
          onClick={() => handleEmbedConfigChange("showBorder", !showBorder)}
          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
            showBorder ? "bg-foreground" : "bg-muted"
          }`}
        >
          <span
            className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
              showBorder ? "translate-x-4" : "translate-x-0"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

/**
 * Inline URL transform for the config panel.
 * MUST stay in sync with transformEmbedUrl() in @provenleap/db/report-layout-types.
 * Duplicated because apps/web cannot depend on @provenleap/db (rootDir constraint).
 */
export function transformUrl(url: string): { embedUrl: string; provider: string } {
  try {
    const parsed = new URL(url);
    const h = parsed.hostname;
    const p = parsed.pathname;

    if (h === "www.youtube.com" || h === "youtube.com" || h === "youtube-nocookie.com") {
      const v = parsed.searchParams.get("v");
      if (v) return { embedUrl: `https://www.youtube.com/embed/${v}`, provider: "YouTube" };
      const sm = p.match(/^\/shorts\/([a-zA-Z0-9_-]+)/);
      if (sm) return { embedUrl: `https://www.youtube.com/embed/${sm[1]}`, provider: "YouTube" };
      if (p.startsWith("/embed/")) return { embedUrl: url, provider: "YouTube" };
    }
    if (h === "youtu.be") {
      const vid = p.slice(1);
      if (vid) return { embedUrl: `https://www.youtube.com/embed/${vid}`, provider: "YouTube" };
    }
    if (h === "vimeo.com" || h === "www.vimeo.com") {
      const vm = p.match(/^\/(\d+)/);
      if (vm) return { embedUrl: `https://player.vimeo.com/video/${vm[1]}`, provider: "Vimeo" };
    }
    if (h === "player.vimeo.com") return { embedUrl: url, provider: "Vimeo" };
    if (h === "docs.google.com" && p.startsWith("/spreadsheets/d/")) {
      const id = p.split("/")[3];
      if (id) return { embedUrl: `https://docs.google.com/spreadsheets/d/${id}/pubhtml`, provider: "Google Sheets" };
    }
    if (h === "docs.google.com" && p.startsWith("/presentation/d/")) {
      const id = p.split("/")[3];
      if (id) return { embedUrl: `https://docs.google.com/presentation/d/${id}/embed`, provider: "Google Slides" };
    }
    if (h === "docs.google.com" && p.startsWith("/document/d/")) {
      const id = p.split("/")[3];
      if (id) return { embedUrl: `https://docs.google.com/document/d/${id}/pub`, provider: "Google Docs" };
    }
    if (h === "www.loom.com" || h === "loom.com") {
      const lm = p.match(/^\/share\/([a-zA-Z0-9]+)/);
      if (lm) return { embedUrl: `https://www.loom.com/embed/${lm[1]}`, provider: "Loom" };
      if (p.startsWith("/embed/")) return { embedUrl: url, provider: "Loom" };
    }
    if (h === "www.figma.com" || h === "figma.com") {
      if (p.startsWith("/file/") || p.startsWith("/design/"))
        return { embedUrl: `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`, provider: "Figma" };
    }
    if ((h === "www.canva.com" || h === "canva.com") && p.startsWith("/design/"))
      return { embedUrl: url, provider: "Canva" };
    if (h === "miro.com" || h === "www.miro.com") {
      const mm = p.match(/^\/app\/board\/([a-zA-Z0-9_=-]+)/);
      if (mm) return { embedUrl: `https://miro.com/app/live-embed/${mm[1]}/`, provider: "Miro" };
    }
    if (h === "notion.so" || h === "www.notion.so") return { embedUrl: url, provider: "Notion" };
    if (h === "airtable.com" || h === "www.airtable.com") return { embedUrl: url, provider: "Airtable" };
    if (h === "calendly.com" || h === "www.calendly.com") return { embedUrl: url, provider: "Calendly" };
    if (h === "typeform.com" || h === "www.typeform.com") return { embedUrl: url, provider: "Typeform" };
  } catch { /* fall through */ }
  return { embedUrl: url, provider: "Unknown" };
}
